import type { FilterFn } from '@tanstack/react-table';

// Advanced filter function that handles different filter types
export const advancedFilter: FilterFn<any> = (row, columnId, filterValue) => {
  const cellValue = row.getValue(columnId);

  // Handle null/undefined values
  if (cellValue == null) {
    return false;
  }

  const cellStr = String(cellValue).toLowerCase();

  // Handle simple string filter (backward compatibility)
  if (typeof filterValue === 'string') {
    return cellStr.includes(filterValue.toLowerCase());
  }

  // Handle advanced filter object
  if (typeof filterValue === 'object' && filterValue.type) {
    const { type, value, value2 } = filterValue;
    const searchValue = String(value || '').toLowerCase();
    const searchValue2 = String(value2 || '').toLowerCase();

    switch (type) {
      case 'contains':
        return cellStr.includes(searchValue);

      case 'not_contains':
        return !cellStr.includes(searchValue);

      case 'starts_with':
        return cellStr.startsWith(searchValue);

      case 'ends_with':
        return cellStr.endsWith(searchValue);

      case 'equals':
        return cellStr === searchValue;

      case 'not_equals':
        return cellStr !== searchValue;

      case 'between':
        // For text: alphabetical comparison
        // For numbers: numeric comparison
        if (!value || !value2) return false;

        const isNumeric = !isNaN(Number(cellValue)) && !isNaN(Number(value)) && !isNaN(Number(value2));

        if (isNumeric) {
          const cellNum = Number(cellValue);
          const min = Math.min(Number(value), Number(value2));
          const max = Math.max(Number(value), Number(value2));
          return cellNum >= min && cellNum <= max;
        } else {
          // Alphabetical comparison
          const min = searchValue < searchValue2 ? searchValue : searchValue2;
          const max = searchValue > searchValue2 ? searchValue : searchValue2;
          return cellStr >= min && cellStr <= max;
        }

      default:
        return true;
    }
  }

  return true;
};

/**
 * Filter function for unique values dropdown
 * Filters rows based on selected values from dropdown
 * Also handles string filter values for backward compatibility
 *
 * Properly typed as FilterFn<any> so TanStack Table handles autoRemove correctly.
 */
export const uniqueValuesFilter: FilterFn<any> = (row, columnId, filterValue) => {
  // Show all rows when no filter is applied
  if (filterValue === undefined || filterValue === null) {
    return true;
  }

  if (Array.isArray(filterValue) && filterValue.length === 0) {
    return true;
  }

  const cellValue = row.getValue(columnId);
  const cellStr = cellValue === null || cellValue === undefined ? '' : String(cellValue);

  // Handle array filter value (multiselect dropdown)
  if (Array.isArray(filterValue)) {
    return filterValue.includes(cellStr);
  }

  // Handle string filter value (text input - backward compatibility)
  if (typeof filterValue === 'string') {
    if (filterValue === '') return true;
    return cellStr.toLowerCase().includes(filterValue.toLowerCase());
  }

  return true;
};

// Set autoRemove so TanStack Table knows when to clear the filter
uniqueValuesFilter.autoRemove = (val: any) => {
  if (val === undefined || val === null) return true;
  if (Array.isArray(val) && val.length === 0) return true;
  if (typeof val === 'string' && val === '') return true;
  return false;
};

advancedFilter.autoRemove = (val: any) => {
  if (val === undefined || val === null) return true;
  if (typeof val === 'string' && val === '') return true;
  if (typeof val === 'object' && val.type && (!val.value && val.value !== 0)) return true;
  return false;
};

// Export filter function for registration
export const filterFns = {
  advancedFilter,
  uniqueValuesFilter,
};
